import React, { useState } from "react";
import { X, Bot, User, Users, Calendar, StickyNote, MessageSquarePlus, ExternalLink, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { tasksApi } from "../lib/api";
import { formatApiErrorDetail } from "../contexts/AuthContext";

const fmtDate = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; }
};
const fmtStamp = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; }
};

export const taskAssigneeNames = (task) =>
  (Array.isArray(task.assigneeNames) && task.assigneeNames.length)
    ? task.assigneeNames
    : (task.assigneeName ? [task.assigneeName] : []);

const taskAssigneeIds = (task) =>
  (Array.isArray(task.assigneeIds) && task.assigneeIds.length)
    ? task.assigneeIds
    : (task.assigneeId ? [task.assigneeId] : []);

/**
 * Reusable task detail modal — opened from the Tasks page and the Customer file.
 * Supports adding progress notes (mirrored to the linked customer) and editing
 * a multi-person assignee list (single shared status).
 */
export default function TaskDetailModal({ task: initialTask, users = [], customer, canManage, canNote, onClose, onOpenCustomer, onUpdated }) {
  const [task, setTask] = useState(initialTask);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingAssignees, setEditingAssignees] = useState(false);
  const [selIds, setSelIds] = useState(taskAssigneeIds(initialTask));
  const noteLog = (task.noteLog || []).slice().reverse();
  const isDone = task.status === "done";
  const names = taskAssigneeNames(task);

  const notifyChanged = () => window.dispatchEvent(new Event("tasks-changed"));

  const addNote = async () => {
    const text = note.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const updated = await tasksApi.addNote(task.id, text);
      setTask(updated);
      onUpdated && onUpdated(updated);
      setNote("");
      toast.success(updated.mirroredToCustomer ? "Note added & saved to customer file" : "Note added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not add note");
    } finally {
      setBusy(false);
    }
  };

  const toggleSel = (id) =>
    setSelIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleStatus = async () => {
    const next = isDone ? "open" : "done";
    if (next === "done" && !window.confirm("Mark this task as complete?")) return;
    setBusy(true);
    try {
      const updated = await tasksApi.update(task.id, { status: next });
      setTask(updated);
      onUpdated && onUpdated(updated);
      notifyChanged();
      toast.success(next === "done" ? "Task completed" : "Task reopened");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not update task");
    } finally {
      setBusy(false);
    }
  };

  const saveAssignees = async () => {
    setBusy(true);
    try {
      const updated = await tasksApi.update(task.id, { assigneeIds: selIds });
      setTask(updated);
      onUpdated && onUpdated(updated);
      setEditingAssignees(false);
      notifyChanged();
      toast.success("Assignees updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not update assignees");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 max-h-[88vh] flex flex-col" data-testid="task-detail-modal">
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isDone ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                {isDone ? "Completed" : "Open"}
              </span>
              {task.source === "automation" && <span className="inline-flex items-center gap-1 text-[10.5px] text-violet-600"><Bot className="h-3 w-3" /> automation</span>}
            </div>
            <h2 className={`text-[16px] font-bold mt-1 ${isDone ? "text-slate-400 line-through" : "text-slate-900"}`} data-testid="task-detail-title">{task.title}</h2>
          </div>
          <button onClick={onClose} data-testid="task-detail-close" className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center shrink-0"><X className="h-4 w-4 text-slate-500" /></button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          {/* Complete / reopen */}
          {canNote && (
            <Button
              onClick={toggleStatus}
              disabled={busy}
              data-testid="task-detail-toggle-status"
              className={`h-9 w-full text-[13px] ${isDone ? "bg-slate-100 text-slate-700 hover:bg-slate-200" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}
            >
              <Check className="h-4 w-4 mr-1.5" /> {isDone ? "Reopen task" : "Mark complete"}
            </Button>
          )}

          {/* Due date */}
          {task.dueAt && (
            <div className="flex items-center gap-1.5 text-[12px] text-slate-600">
              <Calendar className="h-3.5 w-3.5 text-slate-400" /> Due {fmtDate(task.dueAt)}
            </div>
          )}

          {/* Assignees */}
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5">
            <div className="flex items-center justify-between">
              <div className="text-[10.5px] uppercase tracking-wide font-bold text-slate-400 flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" /> Assigned to
              </div>
              {canManage && !editingAssignees && (
                <button onClick={() => { setSelIds(taskAssigneeIds(task)); setEditingAssignees(true); }} data-testid="task-detail-edit-assignees" className="text-[11px] font-semibold text-blue-600 hover:underline">
                  {names.length ? "Reassign" : "Assign"}
                </button>
              )}
            </div>
            {!editingAssignees ? (
              <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="task-detail-assignees">
                {names.length === 0 ? (
                  <span className="text-[12.5px] text-slate-400 italic">Unassigned</span>
                ) : (
                  names.map((n, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-[12px] font-medium px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-700">
                      <User className="h-3 w-3 text-slate-400" /> {n}
                    </span>
                  ))
                )}
              </div>
            ) : (
              <div className="mt-2 space-y-2">
                <div className="max-h-44 overflow-y-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100" data-testid="task-detail-assignee-picker">
                  {users.length === 0 && <div className="px-3 py-2 text-[12px] text-slate-400">No staff available</div>}
                  {users.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 text-[12.5px] cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" checked={selIds.includes(u.id)} onChange={() => toggleSel(u.id)} data-testid={`task-assignee-opt-${u.id}`} className="h-3.5 w-3.5" />
                      <span className="text-slate-700">{u.name}</span>
                    </label>
                  ))}
                </div>
                <div className="flex items-center justify-end gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => setEditingAssignees(false)} disabled={busy} className="h-7 text-[11.5px]">Cancel</Button>
                  <Button size="sm" onClick={saveAssignees} disabled={busy} data-testid="task-detail-save-assignees" className="h-7 text-[11.5px] bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Check className="h-3 w-3 mr-1" /> Save
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Linked customer */}
          {customer && onOpenCustomer && (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5">
              <div className="min-w-0">
                <div className="text-[10.5px] uppercase tracking-wide font-bold text-slate-400">Linked customer</div>
                <button onClick={() => onOpenCustomer(customer.id)} data-testid="task-detail-customer-link" className="text-[13.5px] font-semibold text-blue-600 hover:underline inline-flex items-center gap-1 truncate">
                  {customer.name} <ExternalLink className="h-3 w-3 shrink-0" />
                </button>
              </div>
            </div>
          )}

          {/* Description */}
          {task.notes && (
            <div>
              <div className="text-[10.5px] uppercase tracking-wide font-bold text-slate-400 mb-1">Description</div>
              <div className="text-[13px] text-slate-700 whitespace-pre-wrap break-words">{task.notes}</div>
            </div>
          )}

          {/* Note composer */}
          {canNote && (
            <div>
              <div className="text-[10.5px] uppercase tracking-wide font-bold text-slate-400 mb-1.5">Add an update</div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="task-detail-note-input"
                rows={2}
                placeholder={customer ? `Add a note — also saved to ${customer.name}'s file…` : "Add a progress note…"}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 resize-y"
              />
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-[10.5px] text-slate-400">{customer ? "Saved to this task and the customer's notes" : "Saved to this task"}</span>
                <Button onClick={addNote} disabled={busy || !note.trim()} data-testid="task-detail-add-note-btn" className="h-8 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[12.5px]">
                  <MessageSquarePlus className="h-3.5 w-3.5 mr-1.5" /> {busy ? "Adding…" : "Add note"}
                </Button>
              </div>
            </div>
          )}

          {/* Note log */}
          <div>
            <div className="text-[10.5px] uppercase tracking-wide font-bold text-slate-400 mb-1.5">Updates {noteLog.length > 0 && `· ${noteLog.length}`}</div>
            {noteLog.length === 0 ? (
              <div className="text-[12px] text-slate-400 italic">No updates yet.</div>
            ) : (
              <div className="space-y-2" data-testid="task-detail-notelog">
                {noteLog.map((n) => (
                  <div key={n.id} data-testid={`task-note-${n.id}`} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600">
                      <StickyNote className="h-3 w-3 text-amber-500" />
                      <span>{fmtStamp(n.createdAt)}</span>
                      <span className="text-slate-300">·</span>
                      <span className="text-slate-500 font-medium truncate">{n.createdBy}</span>
                    </div>
                    <div className="text-[13px] text-slate-800 mt-1 whitespace-pre-wrap break-words">{n.text}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
